import { useState, useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MembershipHero from "@/components/membership/MembershipHero";
// SocialProofBar entfernt – 10.000-Aussage lebt jetzt nur noch im Testimonials-Untertitel
import ProblemVisionSection from "@/components/membership/ProblemVisionSection";
import BenefitBlock from "@/components/membership/BenefitBlock";
import PricingThreeTiers from "@/components/membership/PricingThreeTiers";
import MembershipFeatureGrid from "@/components/membership/MembershipFeatureGrid";
import MembershipFinalCTA from "@/components/membership/MembershipFinalCTA";
import MidPageCTA from "@/components/membership/MidPageCTA";
import TestimonialsSection from "@/components/TestimonialsSection";
import { useNavigate } from "react-router-dom";
import { useOptionalSubscription } from "@/contexts/SubscriptionContext";
import { supabase } from "@/integrations/supabase/client";
import { navigateToCheckout } from "@/lib/externalBrowser";
import { type BillingInterval } from "@/config/subscriptionTiers";
import { Badge } from "@/components/ui/badge";
import { Search, Sparkles } from "lucide-react";
import { useIsNativeApp } from "@/hooks/useIsNativeApp";

// Import images
import liederwerkstattImage from "@/assets/teaser-liederwerkstatt.jpg";
import mikoCharacter from "@/assets/miko-character.png";
import musikonzeptCollage from "@/assets/musikonzept-collage.jpg";
import themenreisenHero from "@/assets/themenreisen-hero.png";
import SEO from "@/components/SEO";
import { KURSE_ZAHL, LEKTIONEN_TEXT, gross } from "@/config/webseiteFakten";

const Preise = () => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [billingPeriod] = useState<BillingInterval>("monthly");
  const navigate = useNavigate();
  const subscription = useOptionalSubscription();
  const isNativeApp = useIsNativeApp();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session);
    });
  }, []);

  const currentTier = isLoggedIn && subscription ? subscription.tier : null;
  const isAlreadyMember = currentTier === "pro";

  const handleStartTrial = () => {
    if (isAlreadyMember) return;
    navigateToCheckout(`/abo-checkout/pro?interval=${billingPeriod}`, navigate);
  };

  // In native app, show neutral message instead of pricing page
  if (isNativeApp) {
    return (
      <div className="min-h-screen">
        <SEO title={`Mitgliedschaft & Preise | MusiKonzept`} description={`MusiKonzept Mitgliedschaft: Vollzugriff auf ${LEKTIONEN_TEXT} in über ${KURSE_ZAHL} Kursen, KI-Lernbegleitung und Community. 14 Tage für 1 € testen.`} path="/preise" />
        <Header />
        <div className="container mx-auto px-6 py-24 text-center max-w-lg">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Mitgliedschaft verwalten</h1>
          <p className="text-muted-foreground mb-6">
            Abonnements können über unsere Website verwaltet werden. 
            Besuche musikonzept.de in deinem Browser, um deine Mitgliedschaft zu starten oder zu verwalten.
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />

      {/* 1. Hero */}
      <MembershipHero onStartTrial={handleStartTrial} isAlreadyMember={isAlreadyMember} />

      {/* 2. Drei Säulen – prominent direkt nach dem Hero */}
      <PricingThreeTiers isAlreadyMember={isAlreadyMember} isLoggedIn={isLoggedIn} />

      {/* 3. Problem / Vision Section */}
      <ProblemVisionSection />

      {/* 4. Testimonials (moved up) */}
      <TestimonialsSection />

      {/* 5. Mid-Page CTA */}
      <MidPageCTA onStartTrial={handleStartTrial} isAlreadyMember={isAlreadyMember} />

      {/* 6. Benefit Block: Musik-Bibliothek & Intelligente Suche */}
      <BenefitBlock
        title={`${gross(LEKTIONEN_TEXT)} auf Knopfdruck`}
        description="Lektionen, Lieder, Audios, Videos und PDF-Materialien aus über 250 Kursen – jederzeit verfügbar. Mit unserer intelligenten Suche findest du in Sekunden genau das, was du brauchst. Ob Herbstlieder, Fingerspiele oder Rhythmus-Ideen – alles ist sofort einsetzbar."
        image={musikonzeptCollage}
        imageAlt="MusiKonzept Bibliothek mit Kursen und Materialien"
        highlight="Unbegrenzter Zugang"
      >
        <div className="flex items-center gap-3 p-4 rounded-xl bg-primary/5 border border-primary/10">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Search className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="font-semibold text-foreground">Intelligente Suche</p>
            <p className="text-sm text-muted-foreground">
              Finde Lieder nach Thema, Jahreszeit oder Instrument
            </p>
          </div>
        </div>
      </BenefitBlock>

      {/* 7. Benefit Block: Liederwerkstatt */}
      <div className="bg-muted/20">
        <BenefitBlock
          title="Liederwerkstatt – Jeden Monat neue Inspiration"
          description="Vokalissimo, Gitarrissimo, Ukulissimo – monatliche Live-Sessions mit unseren Expert:innen. Du lernst neue Lieder, bekommst Begleit-Ideen und kannst Fragen direkt stellen. Plus: Alle Aufzeichnungen zum Nachschauen, wann immer du Zeit hast."
          image={liederwerkstattImage}
          imageAlt="Liederwerkstatt Live-Session"
          reverse={true}
          highlight="Live & interaktiv"
        />
      </div>

      {/* 8. Benefit Block: Themenreisen */}
      <BenefitBlock
        title="Themenreisen – Geführte Lernpfade"
        description="Von 'Fußball' bis 'Sommer' – unsere Themenreisen führen dich Schritt für Schritt durch handverlesene Lektionen. Perfekt, wenn du zu einem bestimmten Thema tiefer eintauchen möchtest, ohne selbst lange suchen zu müssen."
        image={themenreisenHero}
        imageAlt="Themenreisen Lernpfade"
        highlight="Kuratierte Inhalte"
      >
        <div className="flex flex-wrap gap-2">
          {["Fußball", "Sommer"].map((tag) => (
            <Badge key={tag} variant="secondary" className="text-sm px-3 py-1">
              {tag}
            </Badge>
          ))}
        </div>
      </BenefitBlock>

      {/* 9. Benefit Block: Miko KI */}
      <div className="bg-muted/20">
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-6">
            <div className="flex flex-col lg:flex-row-reverse items-center gap-12 lg:gap-20">
              <div className="w-full lg:w-1/2 flex justify-center animate-fade-in">
                <div className="relative">
                  <div className="absolute -inset-8 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full blur-3xl opacity-60" />
                  <img
                    src={mikoCharacter}
                    alt="Miko - Dein KI-Musikassistent"
                    className="relative w-64 md:w-80 h-auto drop-shadow-2xl"
                  />
                </div>
              </div>
              <div className="w-full lg:w-1/2 space-y-6 animate-fade-in text-center lg:text-left">
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary bg-primary/10 px-4 py-1.5 rounded-full">
                  <Sparkles className="w-4 h-4" />
                  NEU: KI-Assistent
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground leading-tight">
                  Miko – Dein persönlicher Musik-Assistent
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Hast du eine Frage zu einem Lied? Brauchst du eine Idee für den Morgenkreis? 
                  Miko ist 24/7 für dich da und kennt alle unsere Inhalte. Einfach fragen – 
                  und Miko findet die passende Antwort oder das richtige Material für dich.
                </p>
                <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-accent" />
                    Unbegrenzte Anfragen
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-accent" />
                    Sofortige Antworten
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-accent" />
                    Personalisierte Empfehlungen
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* 10. Feature Grid */}
      <MembershipFeatureGrid />

      {/* 12. Final CTA */}
      <MembershipFinalCTA
        onStartTrial={handleStartTrial}
        isAlreadyMember={isAlreadyMember}
        isLoggedIn={isLoggedIn}
      />

      <Footer />
    </div>
  );
};

export default Preise;
